import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import MultiSegmentProgressBar from 'components/common/MultiSegmentProgressBar';

import formatPrice from 'utils/formatPrice';
import pxToRem from 'styles/pxToRem';

const styles = theme => ({
  typographyCaption: {
    textTransform: 'uppercase',
  },
  gridItem: {
    maxWidth: '20%',
    flexBasis: '20%',
    borderRight: `1px solid ${theme.palette.primary.A100}`,
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
    padding: '8px 16px',
    '&:last-child': {
      borderRight: 'none',
    },
    '&:nth-child(5n)': {
      borderRight: 'none',
    },
  },
  typographyPaid: {
    color: theme.palette.states.paid,
  },
  typographyDenied: {
    color: theme.palette.states.rejected,
  },
  typographyRecouped: {
    color: theme.palette.states.collection,
  },
  typographyRecoverable: {
    color: theme.palette.notification[500],
  },
  typographyPayerName: {
    fontSize: pxToRem(14),
  },
});

const EraChartStats = ({
  filteredEraData,
  classes,
  theme,
}) => {
  const totalClaimsCount = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Paid
        ? filteredEraData[currentValue][eraCurrentValue].Paid['Claims Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Regular']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Claims Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']['Claims Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Recouped
          ? filteredEraData[currentValue][eraCurrentValue].Recouped['Claims Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Denied
        ? filteredEraData[currentValue][eraCurrentValue].Denied['Claims Count'] : 0), 0), 0);
  const totalPatientsCount = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Paid
        ? filteredEraData[currentValue][eraCurrentValue].Paid['Patients Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Regular']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Patients Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']['Patients Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Recouped
        ? filteredEraData[currentValue][eraCurrentValue].Recouped['Patients Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Denied
        ? filteredEraData[currentValue][eraCurrentValue].Denied['Patients Count'] : 0), 0), 0);
  const totalBilledReimbs = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Paid
        ? filteredEraData[currentValue][eraCurrentValue].Paid['Billed Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Regular']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Billed Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']['Billed Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Recouped
        ? filteredEraData[currentValue][eraCurrentValue].Recouped['Billed Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Denied
        ? filteredEraData[currentValue][eraCurrentValue].Denied['Billed Reimbs'] : 0), 0), 0);
  const totalPaidReimbs = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Paid
        ? filteredEraData[currentValue][eraCurrentValue].Paid['Paid Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Regular']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Paid Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']['Paid Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Recouped
        ? filteredEraData[currentValue][eraCurrentValue].Recouped['Paid Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Denied
        ? filteredEraData[currentValue][eraCurrentValue].Denied['Paid Reimbs'] : 0), 0), 0);
  const totalPartialRecoverableAmount = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']['Recoverable Amount'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Regular']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Recoverable Amount'] : 0), 0), 0);
  const totalPartialRecoverableCount = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']
        && filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']['Recoverable Amount']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']['Claims Count'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Regular']
        && filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Recoverable Amount']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Claims Count'] : 0), 0), 0); 
  const totalDeniedReimbs = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Denied
        ? filteredEraData[currentValue][eraCurrentValue].Denied['Billed Reimbs'] : 0), 0), 0);
  const totalDeniedCount = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Denied
        ? filteredEraData[currentValue][eraCurrentValue].Denied['Claims Count'] : 0), 0), 0);
  const totalRecoupedReimbs = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Recouped
        ? filteredEraData[currentValue][eraCurrentValue].Recouped['Paid Reimbs'] : 0), 0), 0);
  const totalRecoupedERACount = Object.keys(filteredEraData).reduce((accumulator, currentValue) =>
    accumulator + Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Recouped
        && filteredEraData[currentValue][eraCurrentValue].Recouped['Paid Reimbs'] !== undefined
        ? 1 : 0), 0), 0);
  const deniedByPayer = Object.keys(filteredEraData).reduce((accumulator, currentValue) => ({
    ...accumulator,
    [currentValue]: Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Denied
        ? filteredEraData[currentValue][eraCurrentValue].Denied['Billed Reimbs'] : 0), 0),
  }), {});
  const billedByPayer = Object.keys(filteredEraData).reduce((accumulator, currentValue) => ({
    ...accumulator,
    [currentValue]: Object.keys(filteredEraData[currentValue]).reduce((eraAccumulator, eraCurrentValue) =>
      eraAccumulator
      + (filteredEraData[currentValue][eraCurrentValue].Paid
        ? filteredEraData[currentValue][eraCurrentValue].Paid['Billed Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Regular']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Regular']['Billed Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']
        ? filteredEraData[currentValue][eraCurrentValue]['Partial Recoverable']['Billed Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Recouped
        ? filteredEraData[currentValue][eraCurrentValue].Recouped['Billed Reimbs'] : 0)
      + (filteredEraData[currentValue][eraCurrentValue].Denied
        ? filteredEraData[currentValue][eraCurrentValue].Denied['Billed Reimbs'] : 0), 0),
  }), {});
  const denialRateByPayer = Object.keys(deniedByPayer).reduce((accumulator, currentValue) => ({
    ...accumulator,
    [currentValue]: (deniedByPayer[currentValue] / billedByPayer[currentValue]) * 100,
  }), {});
  const highestDenialRatePayer = Object.keys(denialRateByPayer).find(key =>
    denialRateByPayer[key] === Math.max(...Object.values(denialRateByPayer)));
  return (
    <Grid container spacing={0}>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Total billed
        </Typography>
        <Typography variant="title">
          {formatPrice(totalBilledReimbs)}
        </Typography>
        <Typography variant="caption">
          submitted
        </Typography>
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Total paid
        </Typography>
        <Typography variant="title" className={classes.typographyPaid}>
          {formatPrice(totalPaidReimbs)}
        </Typography>
        {Object.keys(filteredEraData).length > 0 && (
          <Fragment>
            <MultiSegmentProgressBar
              segments={[
                {
                  value: totalPaidReimbs,
                  color: theme.palette.states.paid,
                },
                {
                  value: totalBilledReimbs - totalPaidReimbs,
                },
              ]}
              height={4}
              gap={1}
              borderRadius={4}
              showLabels
            />
            <Typography variant="caption">
              {((totalPaidReimbs / totalBilledReimbs) * 100).toFixed(2)}% of billed
            </Typography>
          </Fragment>
        )}
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Partial - recoverable
        </Typography>
        <Typography variant="title" className={classes.typographyRecoverable}>
          {formatPrice(totalPartialRecoverableAmount)}
        </Typography>
        <Typography variant="caption">
          {totalPartialRecoverableCount} claims
        </Typography>
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Denied — exposure
        </Typography>
        <Typography variant="title" className={classes.typographyDenied}>
          {formatPrice(totalDeniedReimbs)}
        </Typography>
        <Typography variant="caption">
          {totalDeniedCount} claims
        </Typography>
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Collection rate
        </Typography>
        {Object.keys(filteredEraData).length > 0 && (
          <Typography variant="title">
            {((totalPaidReimbs / totalBilledReimbs) * 100).toFixed(2)}%
          </Typography>
        )}
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Claims
        </Typography>
        <Typography variant="title">
          {totalClaimsCount}
        </Typography>
        <Typography variant="caption">
          total
        </Typography>
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Unique patients
        </Typography>
        <Typography variant="title">
          {totalPatientsCount}
        </Typography>
        <Typography variant="caption">
          across all ERAs
        </Typography>
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Recoupments
        </Typography>
        <Typography variant="title" className={classes.typographyRecouped}>
          {formatPrice(totalRecoupedReimbs)}
        </Typography>
        <Typography variant="caption">
          {totalRecoupedERACount} ERAs with reversals
        </Typography>
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        <Typography variant="caption" className={classes.typographyCaption}>
          Denial rate
        </Typography>
        {Object.keys(filteredEraData).length > 0 && (
          <Typography variant="title">
            {((totalDeniedReimbs / totalBilledReimbs) * 100).toFixed(2)}%
          </Typography>
        )}
      </Grid>
      <Grid className={classes.gridItem} item xs={3}>
        {denialRateByPayer[highestDenialRatePayer] ? (
          <Fragment>
            <Typography variant="caption" className={classes.typographyCaption}>
              Highest denial payer
            </Typography>
            <Typography variant="title" className={classes.typographyPayerName}>
              {highestDenialRatePayer}
            </Typography>
            {Object.keys(filteredEraData).length > 0 && (
              <Typography variant="caption">
                {denialRateByPayer[highestDenialRatePayer] ? denialRateByPayer[highestDenialRatePayer].toFixed(2) : '0.00'}% denial rate
              </Typography>
            )}
          </Fragment>
        ) : ''}
      </Grid>
    </Grid>
  );
};

EraChartStats.propTypes = {
  filteredEraData: PropTypes.objectOf(PropTypes.object).isRequired,
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      states: PropTypes.shape({
        paid: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    typographyCaption: PropTypes.string.isRequired,
    gridItem: PropTypes.string.isRequired,
    typographyPaid: PropTypes.string.isRequired,
    typographyDenied: PropTypes.string.isRequired,
  }).isRequired,
};

export default withStyles(styles, { withTheme: true })(EraChartStats);



// WEBPACK FOOTER //
// ./src/components/home/EraChart/EraChartStats.js